import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

// Deploy sonrası bu URL'i Firebase Functions URL'iyle güncelle
// Örn: https://australia-southeast1-YOUR_PROJECT_ID.cloudfunctions.net/sendContactEmail
const CONTACT_FUNCTION_URL = process.env.REACT_APP_CONTACT_FUNCTION_URL || '';

export const ContactModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;

    setStatus('loading');
    try {
      await axios.post(CONTACT_FUNCTION_URL, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const handleClose = () => {
    setStatus('idle');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg bg-[#0B0E14]/95 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#0B0E14]/95 backdrop-blur-xl border-b border-white/10 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#008080]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#00E5FF]" />
              </div>
              <div>
                <h2 className="font-montserrat font-bold text-xl text-white">{t('contactModal.title')}</h2>
                <p className="text-white/50 text-xs mt-0.5">{t('contactModal.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#10B981]" />
                </div>
                <h3 className="font-montserrat font-bold text-xl text-white mb-2">{t('contactModal.successTitle')}</h3>
                <p className="text-white/60 text-sm mb-6">
                  {t('contactModal.successDesc')}
                </p>
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 bg-[#008080] hover:bg-[#006666] text-white font-semibold rounded-xl transition-colors"
                >
                  {t('contactModal.closeButton')}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm"
                  >
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{t('contactModal.errorMessage')}</span>
                  </motion.div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/60 text-xs font-medium mb-1.5">
                      {t('contactModal.nameLabel')} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder={t('contactModal.namePlaceholder')}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#008080] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-xs font-medium mb-1.5">
                      {t('contactModal.emailLabel')} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder={t('contactModal.emailPlaceholder')}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#008080] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/60 text-xs font-medium mb-1.5">
                    {t('contactModal.subjectLabel')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder={t('contactModal.subjectPlaceholder')}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#008080] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-white/60 text-xs font-medium mb-1.5">
                    {t('contactModal.messageLabel')} <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder={t('contactModal.messagePlaceholder')}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#008080] transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading' || !form.name || !form.email || !form.message}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#008080] hover:bg-[#006666] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      {t('contactModal.sendingButton')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('contactModal.sendButton')}
                    </>
                  )}
                </button>

                <p className="text-center text-white/30 text-xs">
                  {t('contactModal.directEmail')}{' '}
                  <a href="mailto:info@alceylanenterprises.com" className="text-[#00E5FF] hover:underline">
                    info@alceylanenterprises.com
                  </a>
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
